import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ActionButton, MultiSelectList } from '@/shared/components/ui';
import { useUIStore } from '@/stores/uiStore';
import { parseCsv, mapEmployeeCsvRow } from '@/shared/utils/csvParser';

const STORAGE_KEY = 'csas.registrations.employees';

function loadExisting() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch { return []; }
}
function saveAll(arr) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); } catch {}
}

export default function ImportEmployeesModal({ onClose }) {
  const { showSuccessToast } = useUIStore();
  const fileRef = useRef(null);
  const [rawText, setRawText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState(null);

  const chooseFile = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const text = await file.text();
      setRawText(text);
      const { rows } = parseCsv(text, { header: true });
      const mapped = rows.map(mapEmployeeCsvRow).filter(r => r.fullName);
      setParsed(mapped);
      setSelected(new Set(mapped.map((_, i) => i.toString()))); // pre-select all
    } catch (err) {
      setError('Failed to parse CSV. Ensure it is UTF-8 and properly formatted.');
    }
  };

  const items = useMemo(() => (parsed || []).map((r, idx) => ({ id: idx.toString(), label: r.fullName, record: r })), [parsed]);
  const allVisibleSelected = items.length && items.every(i => selected.has(i.id));

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => {
    if (allVisibleSelected) return new Set();
    return new Set(items.map(i => i.id));
  });

  const importSelected = () => {
    if (!parsed) return;
    const existing = loadExisting();
    const chosen = items.filter(i => selected.has(i.id)).map(i => ({
      id: crypto?.randomUUID ? crypto.randomUUID() : `emp-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
      ...i.record,
      createdAt: new Date().toISOString()
    }));
    saveAll([...existing, ...chosen]);
    showSuccessToast('Employees imported', `${chosen.length} employees added.`);
    onClose();
  };

  return (
    <div className="space-y-4 p-3 sm:p-4">
      <div>
        <h2 className="text-lg font-semibold">Import Employees</h2>
        <p className="text-sm text-neutral-500">Upload a CSV file with columns: fullName,email,department,position,site,employmentType</p>
      </div>

      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <ActionButton variant="secondary" onClick={chooseFile}>Choose CSV</ActionButton>
        {parsed && <span className="text-xs text-neutral-500 self-center">{items.length} rows parsed</span>}
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}

      {parsed && (
        <MultiSelectList
          items={items}
          selected={selected}
          getId={(i) => i.id}
            renderLabel={(i) => i.label}
          allVisibleSelected={!!allVisibleSelected}
          onToggle={toggle}
          onToggleAll={toggleAll}
          headerLabel="Employee Name"
          emptyMessage="No valid employee rows."
          height="14rem"
        />
      )}

      <div className="flex flex-col sm:flex-row justify-end gap-3">
        <ActionButton variant="secondary" onClick={onClose}>Cancel</ActionButton>
        <ActionButton variant="primary" disabled={!parsed || !selected.size} onClick={importSelected}>Import {selected.size ? `(${selected.size})` : ''}</ActionButton>
      </div>
    </div>
  );
}
